import {
  type EmbeddedToolLight,
  type Memory,
  type MemoryConstructionParams,
  MemoryConstructionParamsSchema,
  type MemoryGroup,
  type ToolConfig,
  type ToolConfigDb,
} from '@timelyapp/integration-cloud-api-spec';
import { generateDemoMemories } from '@timelyapp/integration-cloud-sdk/memory-demo-data';
import { constructMemoryGroups } from '@timelyapp/integration-cloud-sdk/memory-processors/utils.memories';
import { constructMemoryGroupSummaries } from '@timelyapp/integration-cloud-sdk/memory-processors/utils.memories.summary';
import { constructMemoryLanes } from '@timelyapp/integration-cloud-sdk/memory-processors/utils.memory.lanes';
import { createMockMemory } from '@timelyapp/integration-cloud-sdk/utils/test-mocks';
import { Box, Text, Timeline } from '@timelyapp/react';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import styles from './ToolTimelineExample.module.scss';

{
  /**
   * @todo:
   * x Make reusable, "just feed it Memories" component
   * - Run example memories via processing /w current settings.
   * - Add a "Show more" button to expand the timeline?
   * - Make is sticky/follow along as options are checked??
   */
}
type MockProps = { fromDateTime: DateTime; overrides: Partial<Memory> };

const createMockTitle = (title: string | null, overrides: Partial<Memory>, index = 0) => {
  const tool = overrides.tool || { title: 'Tracker', raw: 'tracker' };
  const memoryOfThings = [
    'document',
    'website',
    'file',
    'message',
    'email',
    'note',
    'task',
    'call',
    'meeting',
  ];
  const prefix = title ? <> title</> : 'Some ';
  return `${prefix} ${memoryOfThings[index] || 'activity'}`;
};

const createMockMemories = ({ fromDateTime, overrides }: MockProps) => {
  const baseDate = fromDateTime.plus({ minutes: 15 }).toJSDate();
  // console.log({ overrides });
  return [
    createMockMemory(
      {
        baseDate,
        startOffsetMins: 10,
        durationMins: 10,
      },
      { title: createMockTitle(null, overrides, 1), ...overrides }
    ),
    createMockMemory(
      {
        baseDate,
        startOffsetMins: 13,
        durationMins: 1,
      },
      { title: createMockTitle(null, overrides, 2), ...overrides }
    ),
    createMockMemory(
      {
        baseDate,
        startOffsetMins: 23,
        durationMins: 5,
      },
      { title: createMockTitle(null, overrides, 3), ...overrides }
    ),
    createMockMemory(
      {
        baseDate,
        startOffsetMins: 25,
        durationMins: 10,
      },
      { title: createMockTitle(null, overrides, 4), ...overrides }
    ),
    createMockMemory(
      {
        baseDate,
        startOffsetMins: 44,
        durationMins: 3,
      },
      { title: createMockTitle(null, overrides, 5), ...overrides }
    ),
    createMockMemory(
      {
        baseDate,
        startOffsetMins: 44,
        durationMins: 1,
      },
      { title: createMockTitle(null, overrides, 5), ...overrides }
    ),
    createMockMemory(
      {
        baseDate,
        startOffsetMins: 32,
        durationMins: 0.5,
      },
      { title: createMockTitle(null, overrides, 1), ...overrides }
    ),
  ];
};

type Props = {
  memories: MemoryGroup[];
  memoryQuery?: MemoryConstructionParams;
  generateMaxMemories?: number;
  tool?: Partial<EmbeddedToolLight>;
  /** This config will inject into `memoryQuery` */
  toolConfig?: ToolConfig | ToolConfigDb;
  /** Set height of demo timeline, in pixels? */
  timelineHeight?: number;
};

const ToolTimelineExample = (props: Props) => {
  const fromDateTime = useMemo(() => DateTime.now().startOf('day').plus({ hours: 7 }), []); // forces predictable seed for faker.js, so dummy memories are always same
  const safeMemoryQuery = useMemo(
    () =>
      MemoryConstructionParamsSchema.parse({
        ...(props.memoryQuery || {}),
        // Forcing sensible value here, to avoid user-defined value breaking the prefixed height.
        // Beware when if/when introducing "zoom level" setting to ToolConfigGlobalDefaults
        pxPerMinute: 4,
        startDate: fromDateTime.toISO(),
        endDate: fromDateTime.plus({ days: 1 }).toISO(),
      }),
    [fromDateTime, props.memoryQuery]
  );

  const { memories, toolConfig, generateMaxMemories = 6 } = props;
  const tool = props.tool || {
    icon_url: 'https://integrations.timelyapp.com/favicon.ico',
    id: 'tracker',
    title: 'Example',
    raw: 'tracker',
  };

  const memoryQuery = useMemo(() => {
    const toolConfigKey =
      toolConfig?.tool_id || toolConfig?.tool_raw || tool?.id || tool?.raw || undefined;

    if (!toolConfig || !toolConfigKey) return safeMemoryQuery;

    // Ensure the embedded tool has all required properties, especially title
    const embeddedToolInConfig = {
      id: (!!toolConfig && 'tool' in toolConfig ? toolConfig?.tool?.id : tool?.id) || toolConfigKey,
      title:
        (!!toolConfig && 'tool' in toolConfig ? toolConfig?.tool?.title : tool?.title) || 'Unknown',
      icon_url: !!toolConfig && 'tool' in toolConfig ? toolConfig?.tool?.icon_url : tool?.icon_url,
      raw: !!toolConfig && 'tool' in toolConfig ? toolConfig?.tool?.raw : tool?.raw,
    };

    return {
      ...safeMemoryQuery,
      minimumDurationMinutes: 0.1,
      minutesPerBlock: 0,
      toolConfigsMap: {
        ...safeMemoryQuery.toolConfigsMap,
        [toolConfigKey]: { ...toolConfig, tool: embeddedToolInConfig },
      },
    } satisfies MemoryConstructionParams;
  }, [safeMemoryQuery, toolConfig]);

  const memoryGroups = useMemo(() => {
    const otherMemories = generateDemoMemories(
      {
        enabled: true,
        preset: 'office',
        calendarTool: 'google_calendar',
        meetingTool: 'zoom',
        projectManagementTool: 'linear',
      },
      {
        maxMemories: generateMaxMemories,
        date: fromDateTime.toISODate(),
        offsetStartNumHours: tool ? 0.5 : 0,
      }
    ).entries;

    const mockMemories = tool
      ? createMockMemories({ fromDateTime, overrides: { tool, icon_url: tool?.icon_url } })
      : [];

    // @ts-expect-error look closer at this type gen inconsistency
    const finalMemories: Memory[] = props.memories.length
      ? [...props.memories, ...otherMemories]
      : [...mockMemories, ...otherMemories];

    return constructMemoryGroups(finalMemories, memoryQuery);
  }, [memories, memoryQuery, tool, fromDateTime, generateMaxMemories]);

  const memoryLanes = useMemo(() => {
    return constructMemoryLanes(memoryGroups, memoryQuery);
  }, [memoryQuery, memoryGroups]);

  const isSticky = !!toolConfig?.preferences?.sticky;
  const isDedicatedLane = !!toolConfig?.preferences?.dedicated_lane;
  const showExtraLaneOfMemories = isSticky || isDedicatedLane;

  // Generate summary blocks
  const summaryBlocks = useMemo(() => {
    if (!memoryQuery.minutesPerBlock) return { blocks: [] };

    // Generate summary blocks from the combined memory groups
    return constructMemoryGroupSummaries(memoryGroups, memoryQuery);
  }, [memoryQuery, memoryGroups]);

  const renderLanes = useCallback(
    (sticky: boolean = false) => {
      return memoryLanes
        .filter(lane => lane.sticky === sticky)
        .map((lane, index) => {
          return (
            <Timeline.Lane key={index} separator={showExtraLaneOfMemories ? 'end' : undefined}>
              <Timeline.Lane.Header variant="subtle">
                {lane.title}
                <Box.Spacer />
              </Timeline.Lane.Header>
              <Timeline.Lane.Sublanes>
                {lane.sub_lanes.map((sublane, index) => {
                  const compact = ('compact' in sublane && sublane.compact) || memoryQuery.compact;
                  const minWidth = compact ? 4 * 10 : 100;
                  const maxWidth = compact ? 4 * 10 : undefined;
                  return (
                    <Timeline.Lane
                      variant="subtle"
                      minSizePx={minWidth}
                      maxWidth={maxWidth}
                      key={index}
                    >
                      {sublane.map((memory, index) => {
                        const startedAt = DateTime.fromISO(memory.started_at);
                        const minutesFromStart = startedAt.diff(fromDateTime, 'minutes').minutes;
                        const offsetPx = Math.round(minutesFromStart * memoryQuery.pxPerMinute);
                        const sizePx =
                          Math.round(memory.total_seconds / 60) * memoryQuery.pxPerMinute;

                        const toolIsInFocus =
                          !tool || memory.tool?.id === tool?.id || memory.tool?.raw === tool?.raw;

                        return (
                          <Timeline.Lane.Memory
                            key={index}
                            orientation={'vertical'}
                            offsetPx={offsetPx}
                            style={
                              !toolIsInFocus
                                ? { filter: 'grayscale(100%)', opacity: 0.5 }
                                : undefined
                            }
                          >
                            <Timeline.Lane.Memory.Bar orientation={'vertical'} sizePx={sizePx}>
                              <img
                                src={
                                  memory.icon_url ||
                                  memory.tool?.icon_url ||
                                  'https://integrations.timelyapp.com/favicon.ico'
                                }
                              />
                            </Timeline.Lane.Memory.Bar>
                            {!compact && !memory.compact && (
                              <Timeline.Lane.Memory.Inline>
                                <Text.Label size="small" ellipsis={1}>
                                  {memory.display_title}
                                </Text.Label>
                                <Text.Label size="xxsmall" color="subtle">
                                  {Math.ceil(memory.total_seconds / 60)}m
                                </Text.Label>
                              </Timeline.Lane.Memory.Inline>
                            )}
                          </Timeline.Lane.Memory>
                        );
                      })}
                    </Timeline.Lane>
                  );
                })}
                <Timeline.Lane variant="subtle"></Timeline.Lane>
              </Timeline.Lane.Sublanes>
            </Timeline.Lane>
          );
        });
    },
    [memoryLanes, memoryQuery, fromDateTime, tool]
  );

  const timelineHeight = props.timelineHeight || 5 * 52;

  return (
    <div className={styles.demoContainer} style={{ height: timelineHeight }}>
      <div className={styles.previewButton}>
        <Box.Row gap="small" alignItems="center">
          <Text.Label size="xxsmall" color="subtle">
            PREVIEW
          </Text.Label>
        </Box.Row>
      </div>
      <Timeline
        onZoomToPx={() => {}}
        className={styles.demoTimeline}
        fromDateTime={fromDateTime}
        scrollStartDateTime={fromDateTime}
        orientation={'vertical'}
        pxPerMinute={memoryQuery.pxPerMinute}
        ticksPerInterval={8}
        data-testid="tool-settings_timeline-preview"
      >
        <Timeline.StickyLanes>
          {/* Summary lane */}
          {summaryBlocks.blocks.length > 0 && (
            <Timeline.Lane
              key="memory-summary-lane"
              variant="subtle"
              separator="end"
              data-testid="timeline_summary_lane"
              minSizePx={150}
              maxSizePx={200}
            >
              <Timeline.Lane.Header variant="subtle">
                Summary
                <Box.Spacer />
              </Timeline.Lane.Header>

              <Timeline.Lane.Sublanes>
                <Timeline.Lane variant="subtle">
                  {/* Render each memory block */}
                  {summaryBlocks.blocks.map((block, blockIndex) => {
                    const minutesFromStart = block.startTime.diff(fromDateTime, 'minutes').minutes;
                    const offsetPx = Math.round(minutesFromStart * memoryQuery.pxPerMinute);
                    const blockDurationMinutes = block.endTime.diff(
                      block.startTime,
                      'minutes'
                    ).minutes;
                    const sizePx = Math.round(blockDurationMinutes * memoryQuery.pxPerMinute) - 1;

                    return (
                      <Timeline.Lane.SummaryBlock
                        key={`summary-${block.summaryMemory.id}-${offsetPx}`}
                        offsetPx={offsetPx}
                      >
                        <Timeline.Lane.SummaryBlock.Content
                          orientation="vertical"
                          sizePx={sizePx}
                          color={block.summaryMemory.color}
                        >
                          <Box.Row gap="small">
                            {block.summaryMemory.icon_url && (
                              <img src={block.summaryMemory.icon_url} width={16} height={16} />
                            )}
                            <Text.Label size="xsmall" ellipsis>
                              {block.summaryMemory.display_title || block.summaryMemory.title}
                            </Text.Label>
                            <Box.Spacer />
                            <Text.Label size="xxsmall" color="subtle" ellipsis={1}>
                              {block.startTime.toFormat('h:mm')} - {block.endTime.toFormat('h:mm')}
                            </Text.Label>
                          </Box.Row>
                        </Timeline.Lane.SummaryBlock.Content>
                      </Timeline.Lane.SummaryBlock>
                    );
                  })}
                </Timeline.Lane>
              </Timeline.Lane.Sublanes>
            </Timeline.Lane>
          )}
          {renderLanes(true)}
        </Timeline.StickyLanes>
        {renderLanes()}
      </Timeline>
    </div>
  );
};

export default ToolTimelineExample;
